import { useMemo, useState } from 'react';
import { Button } from '@/ui/Button';
import { Card } from '@/ui/Card';
import { Drawer } from '@/ui/Drawer';
import { Skeleton } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { cn } from '@/lib/cn';
import { FechasEspeciales } from './FechasEspeciales';
import {
  ConmutadorDeVistas, ORDEN_DEL_CALENDARIO, PuntoDeColor, TIPOS_DEL_CALENDARIO, metaDeModalidad,
} from './piezas';
import { useMes, type ElementoDelCalendario, type TipoDeElemento } from './api';

/**
 * Calendario.tsx — la pantalla de `#eventos-calendario`. Módulo 8, opción 5.
 *
 * ## La rejilla ya no se sale de la pantalla
 *
 * El vanilla usaba `grid-template-columns: repeat(7, 1fr)`, y `1fr` no baja del
 * ancho mínimo de su contenido. Con las píldoras en `white-space: nowrap`, la
 * columna crecía hasta que cabía el título entero. Medido el 2026-09-17 con
 * títulos de 36 caracteres: a 375 px, columnas de 26 a 212 px y **el viernes y
 * el sábado enteros fuera de la tarjeta**, recortados por su `overflow: hidden`
 * y sin forma de llegar a ellos. A 1280 px, 157 frente a 212.
 *
 * Ahora es una `<table>` con `table-layout: fixed`: las siete columnas miden lo
 * mismo por construcción, diga lo que diga el título, y el texto se trunca de
 * verdad. Y es la semántica que tiene un mes: filas de semanas, columnas de días.
 *
 * En pantallas estrechas cada celda enseña un punto por tipo en vez de
 * píldoras, que en 40 px no caben; el detalle del día sigue igual.
 *
 * ## Los colores
 *
 * Tintas `-ink`, medidas, que se invierten solas. Ver `TIPOS_DEL_CALENDARIO` y
 * `PuntoDeColor` en `piezas.tsx`.
 */

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const DIAS = [
  ['Dom', 'Domingo'], ['Lun', 'Lunes'], ['Mar', 'Martes'], ['Mié', 'Miércoles'],
  ['Jue', 'Jueves'], ['Vie', 'Viernes'], ['Sáb', 'Sábado'],
] as const;

const EN_PLURAL: Record<TipoDeElemento, [string, string]> = {
  event: ['evento', 'eventos'],
  special: ['fecha especial', 'fechas especiales'],
  birthday: ['cumpleaños', 'cumpleaños'],
  anniversary: ['aniversario laboral', 'aniversarios laborales'],
  matrimonio: ['matrimonio', 'matrimonios'],
  nacimiento_hijo: ['nacimiento', 'nacimientos'],
};

/** «2 eventos y 1 cumpleaños», para el nombre del botón del día. */
function resumen(items: ElementoDelCalendario[]) {
  const partes = ORDEN_DEL_CALENDARIO
    .map((k) => [k, items.filter((i) => i.kind === k).length] as const)
    .filter(([, n]) => n > 0)
    .map(([k, n]) => `${n} ${EN_PLURAL[k][n === 1 ? 0 : 1]}`);
  return partes.length > 1 ? `${partes.slice(0, -1).join(', ')} y ${partes.at(-1)}` : (partes[0] ?? '');
}

/** Lo que se lee en la píldora: el título de un evento o una fecha, o el nombre
 *  de pila de una persona —en una celda el apellido se cortaría igual—. */
function textoCorto(i: ElementoDelCalendario) {
  return i.kind === 'event' || i.kind === 'special' ? i.title : i.title.split(' ')[0];
}

/** Solo un nombre de Bootstrap Icons pasa a `className`: el icono de una fecha
 *  especial lo escribe gestión. */
const iconoSeguro = (icono: string | undefined, respaldo: string) =>
  /^bi-[a-z0-9-]+$/.test(icono ?? '') ? icono! : respaldo;

export function Calendario() {
  const puedeGestionar = Auth.can('eventos', 'manage');
  const hoy = new Date();
  const [anio, setAnio] = useState(hoy.getFullYear());
  const [mes, setMes] = useState(hoy.getMonth() + 1);
  const [diaAbierto, setDiaAbierto] = useState<number | null>(null);
  const [gestionando, setGestionando] = useState(false);
  const consulta = useMes(anio, mes);

  function mover(delta: number) {
    const d = new Date(anio, mes - 1 + delta, 1);
    setAnio(d.getFullYear());
    setMes(d.getMonth() + 1);
  }

  const esEsteMes = anio === hoy.getFullYear() && mes === hoy.getMonth() + 1;
  const items = consulta.data?.items ?? [];
  const porDia = useMemo(() => {
    const m = new Map<number, ElementoDelCalendario[]>();
    for (const i of items) m.set(i.day, [...(m.get(i.day) ?? []), i]);
    return m;
  }, [items]);

  const semanas = useMemo(() => {
    const primero = new Date(anio, mes - 1, 1).getDay();
    const dias = new Date(anio, mes, 0).getDate();
    const celdas: Array<number | null> = [...Array(primero).fill(null), ...Array.from({ length: dias }, (_, i) => i + 1)];
    while (celdas.length % 7) celdas.push(null);
    return Array.from({ length: celdas.length / 7 }, (_, s) => celdas.slice(s * 7, s * 7 + 7));
  }, [anio, mes]);

  const tipos = ORDEN_DEL_CALENDARIO.filter(
    (k) => consulta.data?.incluye_celebraciones || k === 'event' || k === 'special',
  );

  return (
    <>
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-6 font-bold text-brand-ink">
            <i className="bi bi-calendar3" aria-hidden="true" />
            Calendario corporativo
          </h1>
          <p className="mt-1 text-3 text-muted">
            Eventos, fechas especiales y celebraciones del mes. Pulsa un día para ver el detalle.
          </p>
        </div>
        {puedeGestionar && (
          <Button variant="secondary" onClick={() => setGestionando(true)}>
            <i className="bi bi-stars" aria-hidden="true" /> Fechas especiales
          </Button>
        )}
      </header>

      <ConmutadorDeVistas activa="calendario" />

      <Card className="p-3 sm:p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" iconOnly aria-label="Mes anterior" onClick={() => mover(-1)}>
              <i className="bi bi-chevron-left" aria-hidden="true" />
            </Button>
            <h2 className="min-w-44 text-center text-4 font-bold text-brand-ink first-letter:uppercase" aria-live="polite">
              {MESES[mes - 1]} de {anio}
            </h2>
            <Button variant="ghost" size="sm" iconOnly aria-label="Mes siguiente" onClick={() => mover(1)}>
              <i className="bi bi-chevron-right" aria-hidden="true" />
            </Button>
          </div>
          {!esEsteMes && (
            <Button variant="ghost" size="sm" onClick={() => { setAnio(hoy.getFullYear()); setMes(hoy.getMonth() + 1); }}>
              Volver a hoy
            </Button>
          )}
        </div>

        <ul className="mb-3 flex flex-wrap gap-x-4 gap-y-1.5 text-2 text-ink-2" aria-label="Leyenda">
          {tipos.map((k) => (
            <li key={k} className="flex items-center gap-1.5">
              <span aria-hidden="true" className={cn('size-2.5 rounded-full', TIPOS_DEL_CALENDARIO[k].punto)} />
              {TIPOS_DEL_CALENDARIO[k].grupo}
            </li>
          ))}
        </ul>

        {consulta.isLoading ? (
          <Skeleton className="h-96 w-full" />
        ) : consulta.isError ? (
          <EstadoError err={consulta.error} que="el calendario del mes" onReintentar={() => void consulta.refetch()} />
        ) : (
          // `design-system.css` estiliza TODA tabla del documento: `td` con 12×16 px
          // de relleno y filete, `th` en mayúsculas con fondo, y la fila gris al
          // pasar por encima. Medido el 2026-09-17 a 375 px: en una columna de
          // 41 px el relleno dejaba **11 px** al botón del día, y los puntos de
          // color se aplastaban a 0. Se anula aquí: las clases de `td`/`th` ganan
          // a su selector de elemento, y el fondo del `th` en oscuro y el de la
          // fila al pasar necesitan la especificidad de las dos variantes.
          <table
            className="w-full table-fixed border-separate border-spacing-1
                       [&_tbody_tr:hover_td]:bg-transparent [&_thead_th]:bg-transparent"
            aria-busy={consulta.isFetching}
          >
            <caption className="sr-only">{`${MESES[mes - 1]} de ${anio}`}</caption>
            <thead>
              <tr>
                {DIAS.map(([corto, largo]) => (
                  <th
                    key={corto}
                    scope="col"
                    abbr={largo}
                    className="border-0 p-0 pb-1 text-center text-1 font-bold normal-case tracking-normal text-muted"
                  >
                    {corto}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {semanas.map((semana, s) => (
                <tr key={s}>
                  {semana.map((dia, d) => (
                    <td key={d} className="h-20 border-0 p-0 align-top sm:h-24">
                      {dia != null && (
                        <Dia
                          dia={dia}
                          mes={mes}
                          items={porDia.get(dia) ?? []}
                          esHoy={esEsteMes && dia === hoy.getDate()}
                          onAbrir={() => setDiaAbierto(dia)}
                        />
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <DetalleDelDia
        dia={diaAbierto}
        mes={mes}
        anio={anio}
        items={diaAbierto != null ? porDia.get(diaAbierto) ?? [] : []}
        onCerrar={() => setDiaAbierto(null)}
      />
      {puedeGestionar && (
        <FechasEspeciales abierto={gestionando} anioSugerido={anio} onCerrar={() => setGestionando(false)} />
      )}
    </>
  );
}

function Dia({
  dia, mes, items, esHoy, onAbrir,
}: {
  dia: number;
  mes: number;
  items: ElementoDelCalendario[];
  esHoy: boolean;
  onAbrir: () => void;
}) {
  const numero = (
    <span
      className={cn(
        'grid size-6 place-items-center rounded-full text-2 font-bold',
        esHoy ? 'bg-brand text-on-brand' : 'text-ink-2',
      )}
    >
      {dia}
    </span>
  );
  const caja = cn(
    'flex size-full min-w-0 flex-col gap-1 rounded-sm border p-1 text-left',
    esHoy ? 'border-brand-ink' : 'border-line',
  );

  if (!items.length) {
    return (
      <div className={caja} aria-current={esHoy ? 'date' : undefined}>
        {numero}
      </div>
    );
  }

  const tiposDelDia = ORDEN_DEL_CALENDARIO.filter((k) => items.some((i) => i.kind === k));
  return (
    <button
      type="button"
      onClick={onAbrir}
      aria-current={esHoy ? 'date' : undefined}
      aria-label={`${dia} de ${MESES[mes - 1]}: ${resumen(items)}`}
      className={cn(caja, 'bg-surface transition-colors hover:border-brand-ink hover:bg-sunken focus-visible:focus-anillo')}
    >
      {numero}
      {/* Estrecho: un punto por tipo. */}
      <span className="flex flex-wrap gap-1 px-0.5 sm:hidden" aria-hidden="true">
        {tiposDelDia.map((k) => (
          <span key={k} className={cn('size-2 rounded-full', TIPOS_DEL_CALENDARIO[k].punto)} />
        ))}
      </span>
      {/* Ancho: hasta tres píldoras, truncadas de verdad gracias a `table-fixed`.
          `max-sm:hidden` y no `hidden sm:flex`: el `.hidden` del vanilla lleva
          `!important` y no se revelaría en ningún ancho (React.md, octava trampa). */}
      <span className="flex min-w-0 flex-col gap-0.5 max-sm:hidden" aria-hidden="true">
        {items.slice(0, 3).map((i) => {
          const t = TIPOS_DEL_CALENDARIO[i.kind];
          return (
            <span
              key={`${i.kind}-${i.id}`}
              className={cn('flex min-w-0 items-center gap-1 rounded-xs px-1 py-px text-1', t.texto, t.tinte)}
            >
              {i.kind === 'special' ? (
                <PuntoDeColor color={i.color} className="size-2" />
              ) : (
                <i className={cn('bi shrink-0', t.icono)} />
              )}
              <span className="truncate">{textoCorto(i)}</span>
            </span>
          );
        })}
        {items.length > 3 && <span className="px-1 text-1 text-muted">+{items.length - 3} más</span>}
      </span>
    </button>
  );
}

function DetalleDelDia({
  dia, mes, anio, items, onCerrar,
}: {
  dia: number | null;
  mes: number;
  anio: number;
  items: ElementoDelCalendario[];
  onCerrar: () => void;
}) {
  const verPersonas = Auth.hasModule('directorio');

  return (
    <Drawer
      abierto={dia != null}
      onCambio={(v) => { if (!v) onCerrar(); }}
      titulo={dia != null ? `${dia} de ${MESES[mes - 1]} de ${anio}` : 'Día'}
      descripcion={items.length ? resumen(items) : undefined}
      ancho="sm"
      pie={<Button variant="ghost" onClick={onCerrar}>Cerrar</Button>}
    >
      <div className="flex flex-col gap-5">
        {ORDEN_DEL_CALENDARIO.map((k) => {
          const del = items.filter((i) => i.kind === k);
          if (!del.length) return null;
          const t = TIPOS_DEL_CALENDARIO[k];
          return (
            <section key={k} aria-labelledby={`dia-${k}`}>
              <h3 id={`dia-${k}`} className={cn('mb-2 flex items-center gap-1.5 text-3 font-bold', t.texto)}>
                <i className={cn('bi', t.icono)} aria-hidden="true" /> {t.grupo} ({del.length})
              </h3>
              <ul className="flex flex-col divide-y divide-line rounded-sm border border-line">
                {del.map((i) => (
                  <li key={`${i.kind}-${i.id}`} className="px-3 py-2.5">
                    <Elemento i={i} verPersonas={verPersonas} />
                  </li>
                ))}
              </ul>
            </section>
          );
        })}
      </div>
    </Drawer>
  );
}

function Elemento({ i, verPersonas }: { i: ElementoDelCalendario; verPersonas: boolean }) {
  const enlace =
    'mt-1 inline-flex min-h-8 items-center gap-1 rounded-xs text-2 font-semibold text-brand-ink ' +
    'hover:underline focus-visible:focus-anillo pointer-coarse:min-h-11';

  if (i.kind === 'event') {
    const m = metaDeModalidad(i.modality);
    return (
      <>
        <p className="font-semibold text-ink [overflow-wrap:anywhere]">{i.title}</p>
        <p className="text-2 text-muted">
          {[i.time, m.etiqueta, i.location].filter(Boolean).join(' · ')}
        </p>
        {/* `#eventos/<id>` abre el detalle: antes, desde el calendario no había
            forma de llegar al evento. */}
        <a href={`#eventos/${i.id}`} className={enlace}>
          Ver el evento <i className="bi bi-arrow-right" aria-hidden="true" />
        </a>
      </>
    );
  }

  if (i.kind === 'special') {
    return (
      <>
        <p className="flex items-center gap-2 font-semibold text-ink [overflow-wrap:anywhere]">
          <PuntoDeColor color={i.color} />
          <i className={cn('bi', iconoSeguro(i.icon, 'bi-stars'))} aria-hidden="true" />
          {i.title}
        </p>
        {i.description && <p className="text-2 text-muted [overflow-wrap:anywhere]">{i.description}</p>}
        {i.recurring && <p className="text-1 text-muted">Cada año</p>}
      </>
    );
  }

  const cargo = [i.position, i.area].filter(Boolean).join(' · ');
  const detalle =
    i.kind === 'anniversary'
      ? `${i.years} ${i.years === 1 ? 'año' : 'años'} en la empresa${cargo ? ` · ${cargo}` : ''}`
      : cargo;
  return (
    <>
      <p className="font-semibold text-ink [overflow-wrap:anywhere]">{i.title}</p>
      {detalle && <p className="text-2 text-muted">{detalle}</p>}
      {/* El `id` de una persona es de `users`, y `#directorio/<id>` abre su ficha
          desde el 2026-09-11. Solo si se tiene el Directorio. */}
      {verPersonas && (
        <a href={`#directorio/${i.id}`} className={enlace}>
          Ver en el directorio <i className="bi bi-arrow-right" aria-hidden="true" />
        </a>
      )}
    </>
  );
}
